import useLiveRefresh from '../hooks/useLiveRefresh';
import FightDrainStatus from '../components/FightDrainStatus';
import AdminQueuePanel from '../components/AdminQueuePanel';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useWebSocket, MESSAGE_TYPES } from '../context/WebSocketContext';
import styled from 'styled-components';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import Message from '../components/Message';
import * as sensitiveWordsUtils from '../utils/sensitiveWords';
import { useNavigate } from 'react-router-dom';

// 提取敏感词工具函数
const { checkSensitiveWords, filterChineseInput, filterEnglishInput } = sensitiveWordsUtils;

// 样式组件
const Label = styled.label`
  font-weight: bold;
  color: var(--text-secondary);
`;

const ResponsiveLabel = styled.label`
  font-size: 14px;
  color: var(--text-secondary);
  flex-shrink: 0;
  display: inline-block;

  @media (min-width: 769px) {
    width: 120px;
    text-align: right;
    margin-right: 15px;
    line-height: normal;
    padding: 0;
  }

  @media (max-width: 768px) {
    text-align: left;
    margin-left: 0;
    width: 100%;
    line-height: 20px;
    margin-bottom: 8px;
  }
`;

const Input = styled.input`
  padding: 10px 20px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  background: #F5F5F5 !important;
  color: ${({ theme }) => theme.isDarkMode ? '#000000' : theme.text};
  box-sizing: border-box;
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;

  &:focus {
    outline: none;
    border-color: #ff4d4f;
    box-shadow: none;
    background-color: #F5F5F5 !important;
  }

  &:hover {
    background-color: #F5F5F5 !important;
  }

  &::placeholder {
    color: ${({ theme }) => theme.secondaryText};
  }
`;

const TextArea = styled.textarea`
  padding: 10px 20px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  width: 100%;
  min-height: 100px;
  resize: vertical;
  box-sizing: border-box;
  background: #F5F5F5 !important;
  color: ${({ theme }) => theme.isDarkMode ? '#000000' : theme.text};
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;

  &:focus {
    outline: none;
    border-color: #ff4d4f;
    box-shadow: none;
    background-color: #F5F5F5 !important;
  }

  &:hover {
    background-color: #F5F5F5 !important;
  }

  &::placeholder {
    color: ${({ theme }) => theme.secondaryText};
  }
`;

const SelectButton = styled.div`
  padding: 10px 20px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  position: relative;
  background: ${({ theme }) => theme.cardBg};
  color: ${({ theme }) => theme.text};

  &:hover {
    border-color: #ff4d4f;
  }
`;

const Option = styled.div`
  padding: 10px 20px;
  cursor: pointer;
  color: ${({ theme }) => theme.text};

  &:hover {
    background-color: rgba(255, 77, 79, 0.1);
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.cardBg};
  padding: 20px;
  border-radius: 12px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  position: relative;

  @media (max-width: 480px) {
    width: calc(100% - 32px);
    margin: 0 16px;
  }
`;

const ModalTitle = styled.div`
  margin: 0 0 20px 0;
  color: ${({ theme }) => theme.text};
  font-size: 14px;
  font-weight: normal;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: ${({ theme }) => theme.text};
  padding: 0;
  line-height: 1;

  &:hover {
    opacity: 0.6;
  }
`;

const ModalInputGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
`;

const ModalInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  width: 120px;
  background: #F5F5F5 !important;
  color: ${({ theme }) => theme.isDarkMode ? '#000000' : theme.text};

  &:focus {
    outline: none;
    border-color: #ff4d4f;
    box-shadow: none;
    background-color: #F5F5F5 !important;
  }

  &:hover {
    background-color: #F5F5F5 !important;
  }

  &::placeholder {
    color: ${({ theme }) => theme.secondaryText};
  }
`;

const ModalButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const ModalButton = styled.button`
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  border: none;

  &.cancel {
    background: #f5f5f5;
    color: #333;

    &:hover {
      background: #e0e0e0;
    }
  }

  &.confirm {
    background: #ff4d4f;
    color: white;

    &:hover {
      background: #e53e3e;
    }
  }
`;

const Container = styled.div`
  padding: 20px 24px;
  max-width: 100%;
  margin: 0 auto;
  width: 100%;
  color: ${({ theme }) => theme.text};
  min-height: 100vh;
  background-color: ${({ theme }) => theme.background};
  position: relative;
  overflow-x: hidden;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    padding: 18px 24px;
  }

  @media (max-width: 768px) {
    padding: 16px 24px;
  }

  @media (max-width: 480px) {
    padding: 14px 24px;
  }

  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background:
      repeating-linear-gradient(
        45deg,
        ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)'} 0px,
        ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)'} 1px,
        transparent 1px,
        transparent 2px
      ),
      repeating-linear-gradient(
        -45deg,
        ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)'} 0px,
        ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)'} 1px,
        transparent 1px,
        transparent 2px
      ),
      linear-gradient(to bottom,
        ${({ theme }) => theme.cardBg},
        ${({ theme }) => theme.background}
      );
    pointer-events: none;
    z-index: -2;
  }

  &::after {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='2' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
    opacity: ${({ theme }) => theme.isDark ? '0.08' : '0.12'};
    mix-blend-mode: ${({ theme }) => theme.isDark ? 'color-dodge' : 'multiply'};
    pointer-events: none;
    z-index: -1;
  }

  .status-info {
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin-bottom: 30px;
    font-size: 14px;

    .theme-title {
      width: 28px;
      height: 20px;
      font-family: 'HanaMinA';
      font-weight: 600;
      font-size: 14px;
      line-height: 100%;
      letter-spacing: 0%;
      color: ${({ theme }) => theme.isDarkMode ? '#ffffff' : 'var(--text-primary, #111111)'};

      @media (max-width: 480px) {
        width: auto;
        height: auto;
        white-space: nowrap;
        display: inline-block;
      }
    }

    .status {
      width: 56px;
      height: 20px;
      font-family: 'HanaMinA';
      font-weight: 600;
      font-size: 14px;
      line-height: 100%;
      letter-spacing: 0%;
      color: ${({ theme }) => theme.isDarkMode ? '#ffffff' : 'var(--text-primary, #111111)'};

      @media (max-width: 480px) {
        width: auto;
        height: auto;
        white-space: nowrap;
        display: inline-block;
      }
    }

    .theme-content {
      font-family: 'HanaMinA';
      font-weight: 500;
      font-size: 64px;
      line-height: 120%;
      letter-spacing: 0%;
      color: ${({ theme }) => theme.isDarkMode ? '#ffffff' : 'var(--text-primary, #111111)'};
      display: flex;
      align-items: center;

      @media (max-width: 768px) {
        font-size: 48px;
        width: 100%;
        height: auto;
        white-space: nowrap;
        overflow: hidden;
      }

      @media (max-width: 480px) {
        font-size: 32px;
        width: 100%;
        height: auto;
        white-space: nowrap;
        overflow: hidden;
      }
    }

    .timer {
      font-size: 24px;
      font-weight: bold;
      color: ${({ theme }) => theme.text};
      font-family: 'HanaMinA';

      &.timer-paused {
        color: #ff4d4f !important;
      }
    }

    .status, .online-count {
      font-size: 14px;
      color: ${({ theme }) => theme.text};
      font-family: 'HanaMinA';
    }
  }
`;

const Title = styled.h1`
  text-align: center;
  color: var(--text-primary);
  margin-bottom: 30px;

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 20px;
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 0;
  padding: 0 2px;

  @media (max-width: 768px) {
    gap: 12px;
    margin-bottom: 0;
  }
`;

const Tab = styled.div`
  padding: 10px 0;
  cursor: pointer;
  color: ${({ $active, theme }) => $active
    ? theme.isDarkMode ? '#ffffff' : '#000000'
    : theme.isDarkMode ? '#aaaaaa' : '#666666'
  };
  position: relative;
  transition: all 0.3s ease;
  font-weight: ${({ $active }) => $active ? 'bold' : 'normal'};
  margin-right: ${({ $isOperation }) => $isOperation ? '0' : '0'};

  &:hover {
    color: ${({ theme }) => theme.isDarkMode ? '#ffffff' : '#000000'};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  padding: 20px;
  border-radius: 12px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    gap: 12px;
    padding: 15px;
  }

  @media (max-width: 480px) {
    padding: 10px;
    gap: 10px;
    width: 100%;
    overflow-x: hidden;
  }
`;

const InputGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 15px;
  width: 100%;
  box-sizing: border-box;
  overflow: visible;

  label {
    width: 80px;
    flex-shrink: 0;
    text-align: right;
    line-height: 40px;
  }

  & > div {
    flex: 1;
    min-width: 0;
    width: calc(100% - 95px);
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
    margin-bottom: 12px;

    label {
      width: 100%;
      text-align: left;
      line-height: normal;
      margin-bottom: 4px;
      font-size: 12px;
    }

    & > div {
      width: 100%;

      & > div {
        max-width: none;
      }
    }
  }
`;

const PrimaryButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  background-color: #ff4d4f;
  color: white;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  box-shadow: 0 4px 0 #d73435;
  transform: translateY(0);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 0 #d73435;
  }

  &:active {
    transform: translateY(2px);
    box-shadow: 0 2px 0 #d73435;
  }

  &:disabled {
    background-color: #f5f5f5;
    color: #d9d9d9;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const SecondaryButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  background-color: var(--button-secondary-background, white);
  color: var(--button-secondary-text, #333);
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  box-shadow: 0 4px 0 var(--button-secondary-shadow, #e6e6e6);
  transform: translateY(0);
  white-space: nowrap;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 0 var(--button-secondary-shadow, #e6e6e6);
  }

  &:active:not(:disabled) {
    transform: translateY(2px);
    box-shadow: 0 2px 0 var(--button-secondary-shadow, #e6e6e6);
  }

  &:disabled {
    background-color: var(--button-disabled-background, #f5f5f5);
    color: var(--button-disabled-text, #d9d9d9);
    cursor: not-allowed;
    box-shadow: none;
    opacity: 0.7;
  }

  @media (max-width: 480px) {
    padding: 8px 12px;
    font-size: 13px;
    box-shadow: none;
    transform: none !important;

    &:hover:not(:disabled) {
      box-shadow: none;
    }

    &:active:not(:disabled) {
      box-shadow: none;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
  justify-content: center;
  flex-wrap: wrap;
  padding: 0 10px;
  box-sizing: border-box;

  ${PrimaryButton}, ${SecondaryButton} {
    flex: 1;
    min-width: 120px;
    max-width: 200px;
  }

  @media (max-width: 768px) {
    gap: 8px;
    padding: 0 5px;

    ${PrimaryButton}, ${SecondaryButton} {
      flex: 1 1 calc(50% - 8px);
      min-width: auto;
      padding: 12px 10px;
      font-size: 14px;
    }
  }

  @media (max-width: 480px) {
    ${PrimaryButton}, ${SecondaryButton} {
      flex: 1 1 100%;
      max-width: none;
    }
  }
`;

const Card = styled.div`
  background: var(--card-background, #fff);
  border: 1px solid var(--border-color, #eee);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px var(--card-shadow, rgba(0, 0, 0, 0.05));

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const TimeInputContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#E8E8E8'};
  padding: 0 8px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.2)' : '#e0e0e0'};
  height: 36px;
  flex: 1;
  box-sizing: border-box;
  max-width: 120px;
  overflow: visible;
  position: relative;
  z-index: 1;

  @media (max-width: 480px) {
    height: 40px;
    padding: 0 8px;
    gap: 0;
    max-width: 160px;
    min-width: 140px;
  }
`;

const TimeInput = styled(Input)`
  min-width: 45px;
  width: 45px;
  padding: 5px 8px;
  height: 30px;
  font-size: 14px;
  border: none;
  background: ${({ theme }) => theme.isDarkMode ? 'transparent' : '#F5F5F5'} !important;
  color: ${({ theme }) => theme.isDarkMode ? '#ffffff' : '#000000'} !important;
  text-align: center;

  &:focus {
    outline: none;
    box-shadow: none;
    border: none;
    background: ${({ theme }) => theme.isDarkMode ? 'transparent' : '#F5F5F5'} !important;
  }

  &:hover {
    background: ${({ theme }) => theme.isDarkMode ? 'transparent' : '#F5F5F5'} !important;
    border: none;
  }

  &:active {
    background: ${({ theme }) => theme.isDarkMode ? 'transparent' : '#F5F5F5'} !important;
    border: none;
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const TimeSeparator = styled.span`
  color: ${({ theme }) => theme.isDarkMode ? '#ffffff' : theme.text};
  user-select: none;
  font-weight: bold;
  flex-shrink: 0;
  margin: 0;
  padding: 0 1px;
  line-height: 36px;
  height: 100%;
  display: flex;
  align-items: center;
  opacity: ${({ theme }) => theme.isDarkMode ? '0.9' : '1'};
  text-shadow: ${({ theme }) => theme.isDarkMode ? '0 0 1px rgba(255, 255, 255, 0.5)' : 'none'};

  @media (max-width: 480px) {
    padding: 0 2px;
    font-size: 14px;
  }
`;

const TimeArrowContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 2px;
  height: 100%;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 0;
  gap: 2px;

  @media (max-width: 480px) {
    margin-left: 8px;
    gap: 3px;
  }
`;

const TimeArrow = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.6)'};
  font-size: 12px;
  line-height: 1;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.isDarkMode ? '#ffffff' : '#000000'};
  }

  &:active {
    color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.4)'};
  }
`;

const FilePreviewContainer = styled.div`
  margin-top: 10px;
  position: relative;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const PreviewImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background-color 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const FileDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  .file-name {
    font-weight: 500;
    color: ${({ theme }) => theme.text};
  }

  .file-size {
    color: ${({ theme }) => theme.secondaryText};
    font-size: 12px;
  }
`;

const FileActions = styled.div`
  display: flex;
  gap: 10px;
`;

const CustomSelect = styled.div`
  position: relative;
  width: 100%;
`;

const OptionsContainer = styled.div`
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.cardBg};
  border: 2px solid ${({ $isDark }) => $isDark ? '#ffffff' : '#000000'};
  border-radius: 12px;
  padding: 5px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  opacity: ${({ $isOpen }) => $isOpen ? 1 : 0};
  visibility: ${({ $isOpen }) => $isOpen ? 'visible' : 'hidden'};
  transform: ${({ $isOpen }) => $isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.3s ease;
  z-index: 1000;
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 15px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &.preview {
    background-color: #e6f7ff;
    color: #1890ff;

    &:hover {
      background-color: #bae7ff;
    }
  }

  &.delete {
    background-color: #fff1f0;
    color: #ff4d4f;

    &:hover {
      background-color: #ffd4d4;
    }
  }
`;

const FileUploadContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 40px;
  border: 1px solid ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e0e0e0'};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f9f9f9'};
  overflow: hidden;
  padding: 0 20px;
  box-sizing: border-box;

  &:hover {
    border-color: #ff4d4f;
  }

  input[type="file"] {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    opacity: 0;
    cursor: pointer;
  }

  .upload-placeholder {
    display: flex;
    align-items: center;
    gap: 10px;
    color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.65)' : '#888'};
    pointer-events: none;
    font-size: 14px;
    white-space: nowrap;
  }

  @media (max-width: 480px) {
    min-height: 36px;
    padding: 0 15px;

    .upload-placeholder {
      font-size: 13px;
    }
  }
`;

const ImagePreview = styled.div`
  position: relative;
  width: 100%;
  min-height: 200px;
  border-radius: 12px;
  overflow: hidden;
  background: var(--card-background, #fff);

  img {
    width: 100%;
    height: auto;
    display: block;
  }

  .close-icon {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 24px;
    height: 24px;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(0, 0, 0, 0.7);
    }

    &::before,
    &::after {
      content: '';
      position: absolute;
      width: 12px;
      height: 2px;
      background: white;
      transform: rotate(45deg);
    }

    &::after {
      transform: rotate(-45deg);
    }
  }

  .file-info {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 10px;
    background: rgba(0, 0, 0, 0.5);
    color: white;
    font-size: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
`;

const StatusList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 30px;
  padding: 20px;
  border-radius: 12px;
  background: var(--card-background, #fff);
  box-shadow: 0 2px 8px var(--card-shadow, rgba(0, 0, 0, 0.05));

  .status-item {
    display: flex;
    align-items: center;
    gap: 10px;
    color: var(--text-color, #666);
    font-size: 14px;

    &:before {
      content: '';
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background-color: #ff4d4f;
    }
  }
`;

const WorksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  padding: 20px;
  border-radius: 12px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 10px;
    padding: 10px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    padding: 8px;
  }
`;

const WorkCard = styled.div`
  position: relative;
  overflow: hidden;
  break-inside: avoid;
  margin-bottom: 20px;

  .work-image {
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    display: block;
    border-radius: 0;
  }

  .work-info {
    padding: 8px 0 0 0;
  }

  .author {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;

    img {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      object-fit: cover;
    }

    span {
      color: ${({ theme }) => theme.text};
      font-size: 14px;
      font-weight: 500;
    }
  }

  .votes {
    display: flex;
    align-items: center;
    gap: 8px;

    .vote-count {
      color: ${({ theme }) => theme.text};
      font-weight: 500;
      font-size: 14px;
      white-space: nowrap;
    }

    .voters {
      display: flex;
      align-items: center;

      img {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        border: 2px solid ${({ theme }) => theme.cardBg};
        margin-left: -10px;

        &:first-child {
          margin-left: 0;
        }
      }
    }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: ${({ theme }) => theme.secondaryText};
  text-align: center;
  border-radius: 12px;

  .message {
    margin-top: 16px;
    font-size: 16px;
  }
`;

const OnlineUsersPopover = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: ${({ theme }) => theme.cardBg};
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  min-width: 200px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  margin-top: 8px;
  display: ${({ $isVisible }) => $isVisible ? 'block' : 'none'};
`;

const OnlineUsersList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 200px;
  max-height: 300px;
  overflow-y: auto;
  background: ${({ theme }) => theme.cardBg};
  border: 1px solid ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)'};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  margin-top: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const OnlineUser = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: default;
  transition: background-color 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)'};
  }
`;

const OnlineCount = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: background-color 0.2s ease;

  .online-avatars {
    display: flex;
    align-items: center;
  }
`;

const PauseIndicator = styled.span`
  margin-left: 10px;
  color: #ff4d4f;
  font-size: 16px;
  font-family: inherit;
  font-weight: normal;
  white-space: nowrap;
  display: inline-block;
  vertical-align: baseline;

  @media (max-width: 480px) {
    font-size: 14px;
    margin-left: 8px;
  }
`;

// 时间显示相关样式组件
const TimerDisplay = styled.div`
  width: auto;
  min-width: 120px;
  height: 78px;
  font-family: 'Cormorant Garamond', serif;
  font-weight: 500;
  font-size: 64px;
  line-height: 100%;
  letter-spacing: 0.02em;
  text-align: left;
  font-variant-numeric: lining-nums tabular-nums;
  color: ${({ $isPaused, theme }) => $isPaused ? '#ff4d4f' : theme.isDarkMode ? '#ffffff' : 'var(--text-primary, #111111)'};
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 10px 0;
  white-space: nowrap;

  @media (max-width: 480px) {
    width: auto;
    height: auto;
    font-size: 48px;
    margin: 8px 0;
  }
`;

const TimeUnit = styled.span`
  display: inline-block;
`;

const CountdownSeparator = styled.span`
  display: inline-block;
  margin: 0 2px;
  font-weight: 500;
  color: ${({ $isPaused, theme }) => $isPaused ? '#ff4d4f' : theme.isDarkMode ? '#ffffff' : 'var(--text-primary, #111111)'};
  opacity: 1;
  text-shadow: ${({ theme }) => theme.isDarkMode ? '0 0 1px rgba(255, 255, 255, 0.5)' : 'none'};

  @media (max-width: 480px) {
    margin: 0 1px;
  }
`;

// 添加敏感词检查相关的样式组件
const SensitiveWarning = styled.div`
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

// 默认头像
const defaultAvatar = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjxwYXRoIGQ9Ik0yMCAyMXYtMmE0IDQgMCAwIDAtNC00SDhhNCA0IDAgMCAwLTQgNHYyIj48L2NpcmNsZT48L3N2Zz4=';

// 初始表单数据
const initialFormData = {
  fightNameCn: '',    // 主题-中文
  fightNameEn: '',    // 主题-英文
  fightDesc: '',      // 描述
  appendix: '',       // 附件
  plot: '480',        // 作图时间（8分钟）
  upload: '120',      // 上传时间（2分钟）
  vote: '120',        // 投票时间（2分钟）
  uploadPicCount: '1',// 上传数量
  realNameType: '0',  // 实名上传（已废弃，向后兼容）（0：匿名，1：实名）
  realNameUpload: '0',// 实名上传（0：匿名，1：实名）
  realNameVote: '1',  // 实名投票（0：匿名，1：实名）
  voteNameType: '1',  // 实名投票（已废弃，向后兼容）
  voteConfType: '1',  // 投票配置类型（1：自定义，2：每n位参赛者1票）
  voteConf1Num: '1',  // 默认每人每轮一票
  voteConf2Num: '5',   // N人1票的N值
  uploadPwd:''
};

const ThemeInputWrapper = styled.div`
  position: relative;
  width: 100%;

  @media (min-width: 769px) {
    width: 150%;
  }

  @media (max-width: 768px) {
    width: 100%;
  }

  input {
    width: 100%;
  }
`;

const ThemeSuggestions = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.cardBg};
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-top: 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: ${({ $show }) => $show ? 'block' : 'none'};

  @media (max-width: 480px) {
    max-height: 180px;
  }
`;

const ThemeSuggestionItem = styled.div`
  padding: 10px 20px;
  cursor: pointer;
  color: ${({ theme }) => theme.text};
  transition: all 0.2s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'};
  }

  &:first-child {
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
  }

  &:last-child {
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
  }

  .delete-btn {
    opacity: 0;
    transition: opacity 0.2s ease;
    padding: 4px 8px;
    border-radius: 4px;
    color: #ff4d4f;
    background: transparent;
    border: none;
    cursor: pointer;

    &:hover {
      background: rgba(255, 77, 79, 0.1);
    }
  }

  &:hover .delete-btn {
    opacity: 1;
  }
`;

const ClearHistoryButton = styled.button`
  width: 100%;
  padding: 8px;
  text-align: center;
  color: #ff4d4f;
  background: transparent;
  border: none;
  border-top: 1px solid ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 77, 79, 0.1);
  }
`;

const OptionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  width: 100%;

  @media (max-width: 768px) {
    gap: 12px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    width: 100%;

    .has-input {
      display: inline-flex;
      flex-direction: row;
      align-items: center;
      width: auto;
      flex-wrap: nowrap;

      input[type="text"] {
        margin: 0 2px !important;
        min-width: 36px !important;
        width: 36px !important;
      }

      span {
        white-space: nowrap;
        font-size: 12px;
      }
    }
  }
`;

const RadioWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: 0;
  padding: 4px;
  font-size: 14px;
  color: ${({ theme }) => theme.text};
  min-height: 32px;
  box-sizing: border-box;

  input[type="radio"] {
    appearance: none;
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    border: 2px solid ${({ theme }) => theme.text};
    border-radius: 50%;
    outline: none;
    margin: 0;
    padding: 0;
    cursor: pointer;
    position: relative;
    background: transparent;
    flex-shrink: 0;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &:checked {
      background: transparent;
      border: 2px solid ${({ theme }) => theme.text};
      background-color: transparent !important;

      &:after {
        content: '';
        position: absolute;
        width: 8px;
        height: 8px;
        background: ${({ theme }) => theme.text};
        border-radius: 50%;
      }
    }

    &:focus, &:hover, &:active {
      outline: none;
      box-shadow: none !important;
      border-color: ${({ theme }) => theme.text} !important;
    }
  }

  label {
    cursor: pointer;
    padding: 0;
    margin: 0 0 0 4px;
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
  }

  &.has-input {
    display: inline-flex;
    align-items: center;
    gap: 0;
    width: auto;

    input[type="radio"] {
      margin: 0;
    }

    label {
      margin: 0 0 0 4px;
      flex: 0 0 auto;
      white-space: nowrap;
    }

    ${Input} {
      min-width: 45px;
      width: 45px;
      margin: 0 3px;
      padding: 5px 8px;
      height: 30px;
      font-size: 14px;
      border-radius: 8px;
      flex-shrink: 0;
      text-align: center;
    }

    span {
      white-space: nowrap;
      margin-left: 3px;
      flex-shrink: 0;
    }
  }

  @media (max-width: 480px) {
    font-size: 13px;
    padding: 6px;
    min-height: 36px;

    input[type="radio"] {
      border-width: 1.5px;

      &:checked {
        border: 1.5px solid ${({ theme }) => theme.text};

        &:after {
          width: 7px;
          height: 7px;
        }
      }
    }

    &.has-input {
      ${Input} {
        min-width: 40px;
        width: 40px;
        padding: 4px 6px;
        height: 28px;
        text-align: center;
      }

      label {
        margin: 0 0 0 3px;
      }
    }
  }
`;

const GlobalResponsiveStyles = styled.div`
  /* 覆盖单选框的全局样式 */
  input[type="radio"] {
    box-shadow: none !important;
    outline: none !important;
  }

  input[type="radio"]:checked,
  input[type="radio"]:focus,
  input[type="radio"]:hover,
  input[type="radio"]:active {
    box-shadow: none !important;
    outline: none !important;
  }

  @media (max-width: 480px) {
    .has-input {
      display: flex;
      flex-wrap: nowrap;
      align-items: center;
      margin-bottom: 8px;
      gap: 0 !important;

      input[type="radio"] {
        flex-shrink: 0;
        margin-right: -1px !important;
      }

      label {
        flex-shrink: 1;
        min-width: 0;
        font-size: 13px;
        margin-left: 2px !important;
        padding-left: 0 !important;
      }

      input[type="text"] {
        width: 36px !important;
        min-width: 36px !important;
        height: 28px !important;
        padding: 2px 4px !important;
        margin-left: 2px !important;
        margin-right: 2px !important;
        text-align: center !important;
        background-color: #F5F5F5 !important;
        border: 1px solid #e0e0e0 !important;
        color: #000000 !important;
      }

      span {
        white-space: nowrap;
        font-size: 12px;
        margin-left: 2px !important;
      }
    }
  }
`;

const TimeInputGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 30px;
  width: 100%;
  overflow: visible;

  @media (max-width: 768px) {
    gap: 15px;
  }

  @media (max-width: 480px) {
    gap: 8px;
    grid-template-columns: repeat(3, 1fr);
    margin-bottom: 20px;
    overflow: visible;

    ${InputGroup} {
      margin-bottom: 0;

      label {
        font-size: 12px;
        margin-bottom: 4px;
      }
    }
  }
`;

const FormBottomContainer = styled.div`
  width: calc(100% - 2px);
  max-width: 100%;
  height: auto;
  min-height: 616px;
  margin: 0 1px;
  border-radius: 36px;
  background: ${({ theme }) => theme.isDarkMode
    ? 'rgba(30, 30, 30, 0.6)'
    : 'var(--bg-body-secondary, #E8E8E8)'
  };
  position: relative;
  padding: 30px;
  box-sizing: border-box;
  overflow: visible;
  z-index: 0;
  box-shadow: ${({ theme }) => theme.isDarkMode
    ? '0 4px 20px rgba(0, 0, 0, 0.3)'
    : '0 2px 10px rgba(0, 0, 0, 0.05)'
  };
  border: ${({ theme }) => theme.isDarkMode
    ? '1px solid rgba(255, 255, 255, 0.1)'
    : 'none'
  };
  transition: background 0.3s ease, box-shadow 0.3s ease;

  @media (max-width: 768px) {
    width: calc(100% - 40px);
    border-radius: 24px;
    padding: 20px;
    margin: 0 auto;
    min-height: auto;
  }

  @media (max-width: 480px) {
    width: calc(100% - 30px);
    border-radius: 16px;
    padding: 15px;
    margin: 0 auto;
    min-height: auto;
  }
`;

const FormInnerWrapper = styled.div`
  max-width: 720px;
  width: 100%;
  margin: 0 auto;

  @media (max-width: 480px) {
    overflow-x: hidden; // 移动端隐藏水平溢出
  }
`;

const FileUploadSection = styled.div`
  margin-top: 20px;
  width: 100%;
`;

const ImagePreviewContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 16px;
`;

const ImagePreviewItem = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const DeleteButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
`;

const FileUploadButton = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: ${({ theme }) => theme.primary};
  color: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    opacity: 0.9;
  }

  input {
    display: none;
  }
`;

const WarningText = styled.div`
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DescriptionLabel = styled(Label)`
  font-size: 14px;
  color: var(--text-secondary);
  width: 120px;
  flex-shrink: 0;
  text-align: right;
  margin-right: 15px;
  line-height: 40px;
  white-space: nowrap;

  @media (max-width: 768px) {
    text-align: left;
    margin-bottom: 8px;
  }
`;

const ResponsiveInputGroup = styled(InputGroup)`
  margin-bottom: 20px;
  width: 100%;

  @media (min-width: 769px) {
    flex-direction: row;
    align-items: flex-start;

    ${Input}, ${TextArea} {
      flex: 1;
      max-width: 450px;
    }

    ${TextArea} {
      min-height: 80px;
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;

    ${Input}, ${TextArea} {
      width: 100%;
    }
  }
`;

const FormLabel = styled(Label)`
  font-size: 14px;
  color: var(--text-secondary);
  width: 120px;
  flex-shrink: 0;
  text-align: right;
  margin-right: 15px;
  line-height: 40px;
  white-space: nowrap;

  @media (max-width: 768px) {
    text-align: left;
    margin-bottom: 8px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 15px;
  width: 100%;
  box-sizing: border-box;
  overflow: visible;

  label {
    width: 80px;
    flex-shrink: 0;
    text-align: right;
    line-height: 40px;
  }

  .input-wrapper {
    flex: 1;
    min-width: 0;
    width: calc(100% - 95px);
    max-width: 640px; // 确保与按钮组宽度一致
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 10px;

    label {
      width: 100%;
      text-align: left;
      line-height: 1.5;
    }

    .input-wrapper {
      width: 100%;
    }
  }
`;

const FormInput = styled(Input)`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  background: #F5F5F5 !important;

  input {
    color: #000000 !important;
    background-color: #F5F5F5 !important;

    &:focus {
      border-color: #ff4d4f !important;
      box-shadow: none !important;
      background-color: #F5F5F5 !important;
    }

    &:hover {
      background-color: #F5F5F5 !important;
    }
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const FormTextArea = styled(TextArea)`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  color: #000000 !important;
  background-color: #F5F5F5 !important;

  &:focus {
    background-color: #F5F5F5 !important;
    color: #000000 !important;
    border-color: #ff4d4f !important;
    box-shadow: none !important;
  }

  &:hover {
    background-color: #F5F5F5 !important;
  }
`;

const TimeInputWrapper = styled.div`
  flex: 1;
  max-width: 320px;
  display: flex;
  gap: 10px;
  align-items: center;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
  }
`;

const ActionButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
  max-width: 640px;
  margin: 0;
  padding: 0;

  &.operation-buttons {
    flex-wrap: nowrap;
  }

  &.upload-buttons {
    flex-wrap: wrap;
  }

  @media (max-width: 480px) {
    width: 100%;
    gap: 6px;

    &.operation-buttons {
      ${SecondaryButton} {
        padding: 8px 12px;
        font-size: 13px;
        white-space: nowrap;
        flex: 1;
        min-width: 0;

        &:first-child {
          flex: 1.2;
        }
      }
    }

    &.upload-buttons {
      ${SecondaryButton} {
        padding: 8px 12px;
        font-size: 13px;
        flex: 1 1 calc(50% - 6px);
        min-width: 0;
        white-space: normal;
        text-align: center;
      }
    }
  }
`;

const TimeInputRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  margin-bottom: 15px;
  width: 100%;

  @media (max-width: 480px) {
    display: block;
    margin-bottom: 5px;
  }
`;

const TimeInputColumn = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: 1;
  min-width: 0; /* 防止内容溢出 */

  .time-label {
    width: 120px;
    font-size: 14px;
    color: var(--text-secondary);
    white-space: nowrap;
    text-align: right;
    margin-right: 15px;
  }

  @media (max-width: 480px) {
    display: flex;
    margin-bottom: 15px;

    .time-label {
      font-size: 13px;
      width: 80px;
      margin-right: 10px;
      text-align: left;
      flex-shrink: 0;
    }
  }
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
  width: 100%;

  @media (min-width: 769px) {
    &.theme-input {
      width: 150%;
    }
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const TimeDisplay = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.text};
  font-family: 'HanaMinA';
  font-weight: 500;
  line-height: 36px;
  letter-spacing: 0%;
  text-align: center;
  display: inline-block;
`;

// 添加处理图片URL的函数，与其他页面保持一致
const processImageUrl = (url, isAvatar = false) => {
  // 如果是头像且没有URL或URL为空，返回默认头像
  if (!url && isAvatar) {
    return '/avatars/default.png';
  }

  // 检查URL是否为空或undefined
  if (!url) return '';

  // 如果是base64数据，直接返回
  if (url && url.startsWith('data:')) {
    return url;
  }

  // 如果是头像且URL已经是https://static.battleverse.cn/userpic/格式，直接返回
  if (isAvatar && url && url.includes('https://static.battleverse.cn/userpic/')) {
    return url;
  }

  // 如果是头像且是其他完整URL，直接返回
  if (isAvatar && url && (url.startsWith('http://') || url.startsWith('https://'))) {
    return url;
  }

  // 如果是头像且只是数字，如 "1.png"
  if (isAvatar && url && /^\d+(\.\w+)?$/.test(url)) {
    return `https://static.battleverse.cn/userpic/${url}`;
  }

  // 如果是头像且包含avatars路径，转换为userpic路径
  if (isAvatar && url && (url.includes('avatars/') || url.startsWith('/avatars/'))) {
    const avatarName = url.split('avatars/')[1];
    return `https://static.battleverse.cn/userpic/${avatarName}`;
  }

  // 如果是头像且包含userpic路径但缺少域名
  if (isAvatar && url && (url.includes('userpic/') || url.startsWith('/userpic/'))) {
    const avatarName = url.split('userpic/')[1];
    return `https://static.battleverse.cn/userpic/${avatarName}`;
  }

  // 如果是头像且是其他情况
  if (isAvatar && url) {
    return `https://static.battleverse.cn/userpic/${url}`;
  }

  // 以下是非头像图片的处理逻辑

  // 如果是绝对URL，直接返回
  if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
    return url;
  }

  // 如果是带/前缀的相对路径
  if (url && url.startsWith('/')) {
    return `https://static.battleverse.cn${url}`;
  }

  // 其他情况，添加基础URL
  return url ? `https://static.battleverse.cn/${url}` : '';
};

const BattleSettings = () => {
  // 移除嵌套函数，直接在组件内使用useEffect
  const {
    fightInfo,
    fightStatus,
    lastSecond,
    userCount,
    isConnected,
    sendMessage,
    isStop,
    formatCountdown,
    connect,
    playTimerSound, // 添加 playTimerSound
    fightEndId // 添加 fightEndId
  } = useWebSocket();
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('works');
  const [file, setFile] = useState(null);
  const [showDelayModal, setShowDelayModal] = useState(false);
  const [delaySeconds, setDelaySeconds] = useState('10');
  const [formData, setFormData] = useState({
    ...initialFormData,
    plot: '480',     // 创作时间（8分钟）
    upload: '120',   // 上传时间（2分钟）
    vote: '120'      // 投票时间（2分钟）
  });
  const [openSelect, setOpenSelect] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isImage, setIsImage] = useState(false);
  const [creations, setCreations] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [showOnlineUsers, setShowOnlineUsers] = useState(false);
  const heartbeatInterval = 10000;
  const heartbeatRef = useRef(null);
  const [localLastSecond, setLocalLastSecond] = useState(lastSecond);
  const timerRef = useRef(null);
  const prevFightStatusRef = useRef(fightStatus);
  const [showThemeSuggestions, setShowThemeSuggestions] = useState(false);
  const [themeHistory, setThemeHistory] = useState(() => {
    const saved = localStorage.getItem('themeHistory');
    return saved ? JSON.parse(saved) : [];
  });
  const themeInputRef = useRef(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  // 添加一个 ref 用于文件上传容器
  const fileUploadRef = useRef(null);
  // 添加一个 ref 用于存储状态检查定时器ID
  const statusCheckRef = useRef(null);

  // 添加navigate函数
  const navigate = useNavigate();

  // 组件卸载时清理所有计时器
  useEffect(() => {
    return () => {
      // 清理状态检查计时器
      if (statusCheckRef.current) {
        clearTimeout(statusCheckRef.current);
        statusCheckRef.current = null;
      }

      // 清理心跳定时器
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }

      // 清理倒计时定时器
      if (timerRef.current) {
        cancelAnimationFrame(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  // 确保移动设备上的布局一致性
  useEffect(() => {
    // 添加移动设备上的优化
    const mediaQuery = window.matchMedia('(max-width: 480px)');

    const applyStyles = (matches) => {
      if (matches) {
        // 小屏幕设备优化
        document.querySelectorAll('.has-input input[type="text"]').forEach(input => {
          input.style.width = '36px';
          input.style.minWidth = '36px';
          input.style.padding = '2px 4px';
        });

        document.querySelectorAll('.has-input span').forEach(span => {
          span.style.fontSize = '12px';
          span.style.whiteSpace = 'nowrap';
        });
      } else {
        // 恢复正常样式
        document.querySelectorAll('.has-input input[type="text"]').forEach(input => {
          input.style.width = '';
          input.style.minWidth = '';
          input.style.padding = '';
        });

        document.querySelectorAll('.has-input span').forEach(span => {
          span.style.fontSize = '';
          span.style.whiteSpace = '';
        });
      }
    };

    // 初始应用
    applyStyles(mediaQuery.matches);

    // 监听变化
    mediaQuery.addEventListener('change', (e) => applyStyles(e.matches));

    return () => {
      mediaQuery.removeEventListener('change', (e) => applyStyles(e.matches));
    };
  }, []);

  // 更新本地倒计时和处理状态变化
  useEffect(() => {
    // 检测对战状态变化
    if (fightStatus !== prevFightStatusRef.current) {
      prevFightStatusRef.current = fightStatus;
      // 如果是开始新对战，立即开始倒计时
      if (fightStatus > 0 && fightStatus < 40) {
        setLocalLastSecond(lastSecond);
      }
    } else {
      // 如果是正常的 lastSecond 更新
      setLocalLastSecond(lastSecond);
    }
  }, [lastSecond, fightStatus]);

  // 处理本地倒计时
  useEffect(() => {
    if (timerRef.current) {
      cancelAnimationFrame(timerRef.current);
      timerRef.current = null;
    }

    if (fightStatus > 0 && fightStatus < 40 && !isStop && localLastSecond > 0) {
      let startTime = Date.now();
      let elapsedSeconds = 0;

      const tick = () => {
        const now = Date.now();
        const elapsedMs = now - startTime;
        const newElapsedSeconds = Math.floor(elapsedMs / 1000);

        // 只有当整秒数变化时才更新状态
        if (newElapsedSeconds > elapsedSeconds) {
          const secondsToSubtract = newElapsedSeconds - elapsedSeconds;
          elapsedSeconds = newElapsedSeconds;

          setLocalLastSecond(prev => {
            const newValue = Math.max(0, prev - secondsToSubtract);
            // 如果倒计时结束，清除动画帧
            if (newValue === 0 && timerRef.current) {
              cancelAnimationFrame(timerRef.current);
              timerRef.current = null;
            }
            return newValue;
          });
        }

        // 只有在倒计时大于0且计时器仍在运行时继续
        if (localLastSecond > 0 && !isStop) {
          timerRef.current = requestAnimationFrame(tick);
        }
      };

      // 启动动画帧循环
      timerRef.current = requestAnimationFrame(tick);
    }

    return () => {
      if (timerRef.current) {
        cancelAnimationFrame(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [fightStatus, isStop, localLastSecond]);

  // WebSocket连接状态监控
  useEffect(() => {
    let heartbeatTimer = null;

    const startHeartbeat = () => {
      // 清理之前的心跳定时器
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
      }

      // 发送初始心跳
      try {
        sendMessage({ type: 1 });
        console.log('发送初始心跳消息');
      } catch (error) {
        console.error('初始心跳消息发送失败:', error);
      }

      // 设置新的心跳定时器
      heartbeatTimer = setInterval(() => {
        if (isConnected) {
          try {
            sendMessage({ type: 1 });
            console.log('发送心跳消息');
          } catch (error) {
            console.error('心跳消息发送失败:', error);
            // 如果发送失败，清除定时器
            clearInterval(heartbeatTimer);
          }
        } else {
          // 如果连接断开，清除定时器
          clearInterval(heartbeatTimer);
        }
      }, heartbeatInterval);
    };

    if (isConnected) {
      console.log('WebSocket 已连接，启动心跳');
      startHeartbeat();
    } else {
      console.log('WebSocket 已断开');
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
      }
    }

    return () => {
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
      }
    };
  }, [isConnected, sendMessage, heartbeatInterval]);

  // 监听对战状态变化
  useEffect(() => {
    // 当对战结束时重置所有状态
    if (fightStatus === 40 || fightStatus === 0) {
      // 重置所有状态
      setFormData(initialFormData);
      setFile(null);
      setPreviewUrl(null);
      setIsImage(false);
      setCreations([]);
      setShowDelayModal(false);
      setDelaySeconds('10');
      setOpenSelect(null);
      setShowOnlineUsers(false);
      setLocalLastSecond(0);

      // 清除所有定时器
      if (timerRef.current) {
        cancelAnimationFrame(timerRef.current);
        timerRef.current = null;
      }

      // 清除对战信息
      if (fightInfo) {
        fightInfo.fightName = '';
        fightInfo.fightDesc = '';
        fightInfo.isStop = false;
      }

      // 强制触发一次重新渲染以确保UI更新
      setTimeout(() => {
        setLocalLastSecond(0);
      }, 0);
    }
  }, [fightStatus]);

  const handleBattleAction = async (action, data = {}) => {
    try {
      // 发送请求到服务器
      const response = await axios.post(`//server.battleverse.cn/admin/fight/${action}.do`,
        data,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          withCredentials: false
        }
      );

      if (response.data.success) {
        Message.success(response.data.msg || '操作成功！');

        switch (action) {
          case 'stop':
            if (fightInfo) {
              fightInfo.isStop = true;
            }
            break;

          case 'again':
            if (fightInfo) {
              fightInfo.isStop = false;
            }
            break;

          case 'next':
            // 播放音效
            playTimerSound();

            // 下一阶段处理逻辑
            Message.success('正在切换到下一阶段...',null,2000);

            // 记录当前阶段状态
            const currentStatus = fightStatus;

            // 尝试重新连接WebSocket获取最新状态
            if (connect) {
              try {
                connect(); // 重连WebSocket

                // 开始状态检查
                let checkCount = 0;
                const maxChecks = 10; // 最多检查10次
                const checkInterval = 1000; // 每秒检查一次

                // 清理之前可能存在的定时器
                if (statusCheckRef.current) {
                  clearTimeout(statusCheckRef.current);
                  statusCheckRef.current = null;
                }

                const checkStatusChange = () => {
                  checkCount++;

                  // 如果状态已经变化，则处理成功
                  if (fightStatus !== currentStatus) {
                    if (statusCheckRef.current) {
                      clearTimeout(statusCheckRef.current);
                      statusCheckRef.current = null;
                    }

                    // 特殊处理：如果当前是投票阶段(30)，确保能进入结束阶段(40)
                    if (currentStatus === 30 && fightStatus !== 40) {
                      console.log('从投票阶段切换到结束阶段');
                      // 再次调用next
                      handleBattleAction('next');
                    } else if (fightStatus === 40) {
                      // 当进入到结束阶段(40)时，自动跳转到对战结果页面
                      Message.success('对战已结束，正在前往查看结果...');

                      // 自动跳转到结果页面
                      if (fightInfo && fightInfo.id) {
                        // 验证对战结果是否可用
                        setTimeout(() => {
                          axios.get(`//server.battleverse.cn/battle/detail.do?id=${fightInfo.id}`, {
                            headers: {
                              Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : ''
                            }
                          })
                          .then(response => {
                            if (response.data.success && response.data.code === 0) {
                              navigate(`/battle-result/${fightInfo.id}`);
                            } else {
                              Message.error('无法获取对战结果，请稍后在历史记录中查看');
                            }
                          })
                          .catch(err => {
                            console.error('验证对战结果失败:', err);
                            Message.error('无法获取对战结果，请稍后在历史记录中查看');
                          });
                        }, 2000);
                      }
                    } else {
                      Message.success('已成功进入下一阶段！');
                    }
                    return;
                  }

                  // 如果达到最大检查次数，则可能失败
                  if (checkCount >= maxChecks) {
                    if (statusCheckRef.current) {
                      clearTimeout(statusCheckRef.current);
                      statusCheckRef.current = null;
                    }
                    return;
                  }

                  // 继续检查
                  statusCheckRef.current = setTimeout(checkStatusChange, checkInterval);
                };

                // 启动检查
                statusCheckRef.current = setTimeout(checkStatusChange, checkInterval);

              } catch (err) {
                console.error('WebSocket重连失败', err);
                Message.error('切换阶段失败，请重试');
              }
            }
            break;

          case 'closes':
            // 播放音效
            playTimerSound();

            // 重置所有状态
            setFormData(initialFormData);
            setFile(null);
            setPreviewUrl(null);
            setIsImage(false);
            setCreations([]);
            setShowDelayModal(false);
            setDelaySeconds('10');
            setOpenSelect(null);
            setShowOnlineUsers(false);
            setLocalLastSecond(0);

            // 清除对战信息
            if (fightInfo) {
              fightInfo.isStop = false;
              fightInfo.fightName = '';
              fightInfo.fightDesc = '';
            }

            // 结束整轮对战后直接刷新页面
            Message.success('对战已结束');
            window.location.reload();
            break;
        }
      } else {
        Message.error(response.data.msg || '操作失败！');
      }
    } catch (error) {
      console.error('操作失败:', error);
      Message.error(error.response?.data?.msg || '操作失败！');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // 更新表单数据
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePaste = useCallback(async (e) => {
    e.preventDefault();
    const items = e.clipboardData.items;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          // 阻止触发 input 的 change 事件
          e.stopPropagation();
          setFile(file);
          setIsImage(true);
          const url = URL.createObjectURL(file);
          setPreviewUrl(url);
          break;
        }
      }
    }
  }, []);

  const handleFileChange = useCallback((e) => {
    // 如果是从粘贴触发的，不处理
    if (e.isTrusted === false) {
      return;
    }

    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.type.startsWith('image/')) {
        Message.error('请上传图片文件！');
        e.target.value = '';
        return;
      }
      setFile(selectedFile);
      setIsImage(true);
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
    }
  }, []);

  const handlePreviewFile = () => {
    if (previewUrl) {
      window.open(previewUrl, '_blank');
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setPreviewUrl(null);
    setIsImage(false);
    // 重置文件输入
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // 在组件卸载时清理预览URL
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const uploadFile = async () => {
    if (!file) return null;

    const formData = new FormData();
    formData.append('upfile', file);

    try {
      const response = await axios.post('//server.battleverse.cn/admin/upload.do', formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        return response.data.data.url;
      }
    } catch (error) {
      console.error('文件上传失败:', error);
    }
    return null;
  };

  const handleCreateBattle = async (e) => {
    e.preventDefault();

    // 只验证必填字段（中文和英文主题）
    if (!formData.fightNameCn || !formData.fightNameEn) {
      Message.error('请填写中文和英文主题！');
      return;
    }

    let fileUrl = null;

    try {
      if (!isConnected) {
        Message.error('WebSocket 未连接，请刷新页面');
        return;
      }

      // 保存主题到历史记录
      saveThemeToHistory(formData.fightNameCn);

      // 如果有文件，才进行上传
      if (file) {
        const formData = new FormData();
        formData.append('upfile', file);

        const uploadResponse = await axios.post('//server.battleverse.cn/admin/upload.do', formData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        if (!uploadResponse.data.success) {
          Message.error(uploadResponse.data.msg || '文件上传失败！');
          return;
        }

        fileUrl = uploadResponse.data.data.url;
      }

      // 准备提交的数据
      const submitData = {
        fightNameCn: formData.fightNameCn,       // 中文主题
        fightNameEn: formData.fightNameEn,       // 英文主题
        fightDesc: formData.fightDesc || '',     // 描述为空时提交空字符串
        appendix: fileUrl || '',                 // 附件URL为空时提交空字符串
        plotTime: formData.plot,                 // 作图时间（秒）
        uploadTime: formData.upload,             // 上传时间（秒）
        voteTime: formData.vote,                 // 投票时间（秒）
        uploadPicCount: formData.uploadPicCount, // 每人最多上传作品张数
        realNameUpload: formData.realNameType || '1', // 是否实名上传（0：匿名，1：实名）
        realNameVote: formData.voteNameType || '1',   // 是否实名投票（0：匿名，1：实名）
        voteConfType: formData.voteConfType || '2',   // 投票设置（1：自定义，2：每n位参赛者1票）
        voteConf1Num: (formData.voteConf1Num || '5').replace(/\u200B/g, ''),   // 自定义，每人几票
        voteConf2Num: (formData.voteConf2Num || '5').replace(/\u200B/g, ''),    // 每几位参赛者1票
        uploadPwd:formData.uploadPwd || '' //上传所需的密码
      };

      const response = await axios.post('//server.battleverse.cn/admin/fight/add.do',
        submitData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        Message.success('对战创建成功！', null, 2000);

        const res = await axios.get('//battleverse.cn/vacat2025/backend/api/prompt/add_pwd.php?uploadPwd=' + submitData['uploadPwd']);
        
        // 重置表单状态
        setFormData(initialFormData);
        setFile(null);
        setPreviewUrl(null);
        setIsImage(false);
        
        
        // 等待服务器推送对战开始消息
        console.log('等待服务器推送对战开始消息...');
      } else {
        Message.error(response.data.msg || '创建对战失败！');
      }
    } catch (error) {
      console.error('创建对战失败:', error);
      Message.error(error.response?.data?.msg || '创建对战失败！');
    }
  };

  const handleUpdateBattle = async () => {
    try {
      const response = await axios.post('//server.battleverse.cn/admin/fight/update.do',
        {
          fightNameCn: formData.fightNameCn,
          fightNameEn: formData.fightNameEn,
          fightDesc: formData.fightDesc,
          realNameUpload: formData.realNameType || '1',
          realNameVote: formData.voteNameType || '1'
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        Message.success(response.data.msg || '对战信息更新成功！');
      } else {
        Message.error(response.data.msg || '更新对战失败！');
      }
    } catch (error) {
      console.error('更新对战失败:', error);
      Message.error(error.response?.data?.msg || '更新对战失败！');
    }
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setFile(null);
    // 重置文件输入框
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleSelectClick = (selectName) => {
    setOpenSelect(openSelect === selectName ? null : selectName);
  };

  const handleOptionSelect = (name, value) => {
    console.log('handleOptionSelect', name, value);
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setOpenSelect(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openSelect && !event.target.closest('.custom-select')) {
        setOpenSelect(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openSelect]);

  // 获取状态文本
  const getStatusText = (status) => {
    switch (status) {
      case 0: return '无对战';
      case 10: return '创作阶段';
      case 20: return '上传阶段';
      case 30: return '投票阶段';
      case 40: return '已结束';
    }
  };

  // 获取作品列表
  const fetchCreations = async () => {
    try {
      const response = await axios.post('//server.battleverse.cn/fight/creations.do',
        {},
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      if (response.data.success) {
        setCreations(response.data.data);
      }
    } catch (error) {
      console.error('获取作品列表失败:', error);
    }
  };

  // 监听WebSocket消息
  useEffect(() => {
    if (fightStatus === 20 || fightStatus === 30) {
      fetchCreations();
    }
  }, [fightStatus]);

  useLiveRefresh(fetchCreations, fightStatus === 20 || fightStatus === 30, 5000, 'pb:creations-changed');

  // 获取在线用户列表
  const fetchOnlineUsers = async () => {
    try {
      const response = await axios.post('//server.battleverse.cn/fight/online/users.do',
        {},
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      if (response.data.success) {
        // 处理用户头像URL
        const processedUsers = response.data.data.map(user => ({
          ...user,
          userPic: user.userPic ?
            (user.userPic.startsWith('http') ? user.userPic : `https://static.battleverse.cn${user.userPic}`) :
            user.userPic
        }));
        setOnlineUsers(processedUsers);
      }
    } catch (error) {
      console.error('获取在线用户列表失败:', error);
    }
  };

  // 点击在线人数时获取用户列表
  const handleOnlineCountClick = (e) => {
    e.stopPropagation(); // 阻止事件冒泡
    setShowOnlineUsers(!showOnlineUsers);
    if (!showOnlineUsers) {
      fetchOnlineUsers();
    }
  };

  // 点击其他地方关闭用户列表
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showOnlineUsers && !event.target.closest('.online-count') && !event.target.closest('.online-users-list')) {
        setShowOnlineUsers(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showOnlineUsers]);

  // 定期获取在线用户列表
  useEffect(() => {
    fetchOnlineUsers(); // 初始获取

    const timer = setInterval(() => {
      fetchOnlineUsers();
    }, 5000); // 每5秒更新一次

    return () => clearInterval(timer);
  }, []);

  const handleDelayConfirm = () => {
    const seconds = parseInt(delaySeconds);
    if (isNaN(seconds) || seconds <= 0) {
      Message.error('请输入有效的延时时间！');
      return;
    }
    handleBattleAction('delay', { second: seconds });
    setShowDelayModal(false);
  };

  // 监听WebSocket消息更新表单数据
  useEffect(() => {
    if (isConnected && fightInfo) {
      // 更新表单数据
      const newFormData = {
        fightNameCn: fightInfo.fightNameCn || '',
        fightNameEn: fightInfo.fightNameEn || '',
        fightDesc: fightInfo.fightDesc || '',
        plot: String(fightInfo.plotTime ?? '480'),
        upload: String(fightInfo.uploadTime ?? '120'),
        vote: String(fightInfo.voteTime ?? '120'),
        uploadPicCount: String(fightInfo.uploadPicCount ?? '1'),
        realNameType: String(fightInfo.realNameUpload ?? '0'),
        realNameUpload: String(fightInfo.realNameUpload ?? '0'),
        realNameVote: String(fightInfo.realNameVote ?? '1'),
        voteNameType: String(fightInfo.realNameVote ?? '1'),
        voteConfType: String(fightInfo.voteConfType ?? '1'),
        voteConf1Num: String(fightInfo.voteConf1Num ?? '1'),
        voteConf2Num: String(fightInfo.voteConf2Num ?? '5')
      };

      setFormData(newFormData);
    }
  }, [isConnected, fightInfo]);

  // 添加监听对战状态变化，在对战结束后清空表单数据
  useEffect(() => {
    // 如果是从对战状态变为结束状态，需要清空表单数据
    if (fightStatus === 40) {
      console.log('对战已结束，清空表单数据');
      setFormData({
        fightNameCn: '',
        fightNameEn: '',
        fightDesc: '',
        plot: '480',
        upload: '120',
        vote: '120',
        uploadPicCount: '1',
        realNameType: '0',
        realNameUpload: '0',
        realNameVote: '1',
        voteNameType: '1',
        voteConfType: '1',
        voteConf1Num: '1',
        voteConf2Num: '5'
      });

      // 清除文件
      setFile(null);
      setPreviewUrl(null);
    }
  }, [fightStatus]);

  // 监听在线用户数变化
  useEffect(() => {
    if (isConnected && userCount !== undefined) {
      fetchOnlineUsers();
    }
  }, [isConnected, userCount]);

  // 添加一个格式化时间的函数，用于分离时、分、秒
  const formatTimeWithSeparators = (timeString) => {
    if (typeof timeString !== 'string') {
      timeString = String(timeString);
    }

    if (!timeString.includes(':')) {
      return (
        <>
          <TimeUnit>{timeString}</TimeUnit>
        </>
      );
    }

    const parts = timeString.split(':');

    if (parts.length === 2) {
      return (
        <>
          <TimeUnit>{parts[0]}</TimeUnit>
          <CountdownSeparator $isPaused={isStop === 1}>:</CountdownSeparator>
          <TimeUnit>{parts[1]}</TimeUnit>
        </>
      );
    }

    if (parts.length === 3) {
      return (
        <>
          <TimeUnit>{parts[0]}</TimeUnit>
          <CountdownSeparator $isPaused={isStop === 1}>:</CountdownSeparator>
          <TimeUnit>{parts[1]}</TimeUnit>
          <CountdownSeparator $isPaused={isStop === 1}>:</CountdownSeparator>
          <TimeUnit>{parts[2]}</TimeUnit>
        </>
      );
    }

    return timeString;
  };

  // 保存主题到历史记录
  const saveThemeToHistory = useCallback((theme) => {
    if (!theme) return;

    setThemeHistory(prev => {
      const newHistory = [theme, ...prev.filter(t => t !== theme)].slice(0, 10);
      localStorage.setItem('themeHistory', JSON.stringify(newHistory));
      return newHistory;
    });
  }, []);

  // 处理主题建议点击
  const handleThemeSuggestionClick = (theme) => {
    setFormData(prev => ({
      ...prev,
      fightNameCn: theme
    }));
    setShowThemeSuggestions(false);
  };

  // 添加点击外部关闭建议列表的处理
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (themeInputRef.current && !themeInputRef.current.contains(event.target)) {
        setShowThemeSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 删除单个历史记录
  const handleDeleteTheme = (themeToDelete, e) => {
    e.stopPropagation(); // 阻止事件冒泡，避免触发选择主题
    setThemeHistory(prev => {
      const newHistory = prev.filter(theme => theme !== themeToDelete);
      localStorage.setItem('themeHistory', JSON.stringify(newHistory));
      return newHistory;
    });
  };

  // 清空所有历史记录
  const handleClearHistory = (e) => {
    e.stopPropagation(); // 阻止事件冒泡
    setThemeHistory([]);
    localStorage.removeItem('themeHistory');
    setShowThemeSuggestions(false);
  };

  const handleTimeInputChange = (field, value) => {
    const timeFields = {
      plotTimeMinutes: { unit: 'minutes', field: 'plot' },
      plotTimeSeconds: { unit: 'seconds', field: 'plot' },
      uploadTimeMinutes: { unit: 'minutes', field: 'upload' },
      uploadTimeSeconds: { unit: 'seconds', field: 'upload' },
      voteTimeMinutes: { unit: 'minutes', field: 'vote' },
      voteTimeSeconds: { unit: 'seconds', field: 'vote' }
    };

    // 检查field是否在timeFields对象中存在
    if (!timeFields[field]) {
      console.warn(`Field "${field}" is not defined in timeFields`);
      return;
    }

    const { unit, field: targetField } = timeFields[field];
    const currentTotalSeconds = parseInt(formData[targetField]) || 0;
    const currentMinutes = Math.floor(currentTotalSeconds / 60);
    const currentSeconds = currentTotalSeconds % 60;

    let newValue = parseInt(value) || 0;
    let totalSeconds;

    if (unit === 'minutes') {
      totalSeconds = newValue * 60 + currentSeconds;
    } else {
      totalSeconds = currentMinutes * 60 + newValue;
    }

    setFormData(prev => ({
      ...prev,
      [targetField]: String(totalSeconds)
    }));
  };

  const getTimeInputValue = (timeField, unit) => {
    // 映射timeField到formData中的实际字段
    const fieldMap = {
      'plotTime': 'plot',
      'uploadTime': 'upload',
      'voteTime': 'vote'
    };

    const actualField = fieldMap[timeField] || timeField;
    const totalSeconds = parseInt(formData[actualField]) || 0;

    switch (unit) {
      case 'minutes':
        return String(Math.floor(totalSeconds / 60)).padStart(2, '0');
      case 'seconds':
        return String(totalSeconds % 60).padStart(2, '0');
      default:
        return '00';
    }
  };

  const renderTimeInput = (fieldName, label) => (
    <InputGroup>
      <Label style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
        {label}
      </Label>
      <TimeInputContainer className="time-input-container">
        <TimeInput
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={getTimeInputValue(fieldName, 'minutes')}
          onChange={(e) => {
            const value = e.target.value.replace(/[^0-9]/g, '');
            handleTimeInputChange(`${fieldName}Minutes`, value);
          }}
          onFocus={(e) => e.target.select()}
        />
        <TimeSeparator>:</TimeSeparator>
        <TimeInput
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={getTimeInputValue(fieldName, 'seconds')}
          onChange={(e) => {
            const value = e.target.value.replace(/[^0-9]/g, '');
            handleTimeInputChange(`${fieldName}Seconds`, value);
          }}
          onFocus={(e) => e.target.select()}
        />
        <TimeArrowContainer>
          <TimeArrow
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const totalSeconds = parseInt(formData[fieldName]) || 0;
              const minutes = Math.floor(totalSeconds / 60);
              let newSeconds = (totalSeconds % 60 + 1) % 60;
              const newTotalSeconds = minutes * 60 + newSeconds;
              setFormData(prev => ({
                ...prev,
                [fieldName]: String(newTotalSeconds)
              }));
            }}
            type="button"
          >
            <span style={{ transform: 'translateY(-1px)' }}>▴</span>
          </TimeArrow>
          <TimeArrow
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const totalSeconds = parseInt(formData[fieldName]) || 0;
              const minutes = Math.floor(totalSeconds / 60);
              let newSeconds = (totalSeconds % 60 - 1 + 60) % 60;
              const newTotalSeconds = minutes * 60 + newSeconds;
              setFormData(prev => ({
                ...prev,
                [fieldName]: String(newTotalSeconds)
              }));
            }}
            type="button"
          >
            <span style={{ transform: 'translateY(1px)' }}>▾</span>
          </TimeArrow>
        </TimeArrowContainer>
      </TimeInputContainer>
    </InputGroup>
  );

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);

    // 检查文件类型
    const invalidFiles = files.filter(file => !file.type.startsWith('image/'));
    if (invalidFiles.length > 0) {
      Message.error('只能上传图片文件');
      return;
    }

    // 创建预览
    const newPreviews = await Promise.all(
      files.map(file => ({
        file,
        preview: URL.createObjectURL(file)
      }))
    );

    setPreviewImages(prev => [...prev, ...newPreviews]);
    setUploadedFiles(prev => [...prev, ...files]);
  };

  const handleRemoveImage = (index) => {
    setPreviewImages(prev => {
      const newPreviews = [...prev];
      URL.revokeObjectURL(newPreviews[index].preview);
      newPreviews.splice(index, 1);
      return newPreviews;
    });
    setUploadedFiles(prev => {
      const newFiles = [...prev];
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  // 在组件卸载时清理预览URL
  useEffect(() => {
    return () => {
      previewImages.forEach(item => {
        URL.revokeObjectURL(item.preview);
      });
    };
  }, []);

  const renderOperationContent = () => {
    if (!fightStatus || fightStatus === 0) {
      // 未开始对战时显示设置表单
      return (
        <Form onSubmit={handleCreateBattle} style={{ margin: '0 auto', overflowX: 'hidden' }} noValidate>
          <FormGroup>
            <FormLabel>*主题（中文）</FormLabel>
            <div className="input-wrapper">
              <FormInput
                type="text"
                name="fightNameCn"
                value={formData.fightNameCn}
                onChange={handleInputChange}
                onPaste={(e) => {
                  // 允许粘贴任何文本
                  const pastedText = e.clipboardData.getData('text');
                  if (pastedText) {
                    setFormData(prev => ({
                      ...prev,
                      fightNameCn: pastedText
                    }));
                    e.preventDefault();
                  }
                }}
                placeholder="请输入对战主题（中文）"
              />
            </div>
          </FormGroup>

          <FormGroup>
            <FormLabel>*主题（英文）</FormLabel>
            <div className="input-wrapper">
              <FormInput
                type="text"
                name="fightNameEn"
                value={formData.fightNameEn}
                onChange={handleInputChange}
                onPaste={(e) => {
                  // 允许粘贴任何文本
                  const pastedText = e.clipboardData.getData('text');
                  if (pastedText) {
                    setFormData(prev => ({
                      ...prev,
                      fightNameEn: pastedText
                    }));
                    e.preventDefault();
                  }
                }}
                placeholder="请输入对战主题（英文）"
              />
            </div>
          </FormGroup>

          <FormGroup>
            <FormLabel>描述</FormLabel>
            <div className="input-wrapper">
              <FormTextArea
                name="fightDesc"
                value={formData.fightDesc}
                onChange={handleInputChange}
                onPaste={(e) => {
                  // 允许粘贴文本
                  const pastedText = e.clipboardData.getData('text');
                  if (pastedText) {
                    setFormData(prev => ({
                      ...prev,
                      fightDesc: pastedText
                    }));
                    e.preventDefault();
                  }
                }}
                placeholder="请输入对战描述"
              />
            </div>
          </FormGroup>

          <FormGroup>
            <FormLabel>附件</FormLabel>
            <div className="input-wrapper">
              {!file && (
                <FileUploadContainer
                  style={{ cursor: 'pointer' }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files[0];
                    if (file && file.type.startsWith('image/')) {
                      setFile(file);
                      setIsImage(true);
                      const url = URL.createObjectURL(file);
                      setPreviewUrl(url);
                    }
                  }}
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  <div className="upload-placeholder">
                    复制粘贴或点击上传
                  </div>
                </FileUploadContainer>
              )}
              {file && (
                <FilePreviewContainer>
                  <PreviewImage
                    src={previewUrl}
                    alt={file.name}
                  />
                  <CloseButton onClick={handleRemoveFile}>
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 6L6 18M6 6l12 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </CloseButton>
                </FilePreviewContainer>
              )}
            </div>
          </FormGroup>

          <FormGroup>
            <TimeInputRow>
              <TimeInputColumn>
                <div className="time-label"><strong>创作时间</strong></div>
                <TimeInputContainer className="time-input-container">
                  <TimeInput
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={getTimeInputValue('plotTime', 'minutes')}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      handleTimeInputChange(`plotTimeMinutes`, value);
                    }}
                    onFocus={(e) => e.target.select()}
                  />
                  <TimeSeparator>:</TimeSeparator>
                  <TimeInput
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={getTimeInputValue('plotTime', 'seconds')}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      handleTimeInputChange(`plotTimeSeconds`, value);
                    }}
                    onFocus={(e) => e.target.select()}
                  />
                  <TimeArrowContainer>
                    <TimeArrow
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const totalSeconds = parseInt(formData.plot) || 0;
                        const minutes = Math.floor(totalSeconds / 60);
                        let newSeconds = (totalSeconds % 60 + 1) % 60;
                        const newTotalSeconds = minutes * 60 + newSeconds;
                        setFormData(prev => ({
                          ...prev,
                          plot: String(newTotalSeconds)
                        }));
                      }}
                      className="up"
                    >
                      ▴
                    </TimeArrow>
                    <TimeArrow
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const totalSeconds = parseInt(formData.plot) || 0;
                        const minutes = Math.floor(totalSeconds / 60);
                        let newSeconds = (totalSeconds % 60 - 1 + 60) % 60;
                        const newTotalSeconds = minutes * 60 + newSeconds;
                        setFormData(prev => ({
                          ...prev,
                          plot: String(newTotalSeconds)
                        }));
                      }}
                      className="down"
                    >
                      ▾
                    </TimeArrow>
                  </TimeArrowContainer>
                </TimeInputContainer>
              </TimeInputColumn>

              <TimeInputColumn>
                <div className="time-label"><strong>上传时间</strong></div>
                <TimeInputContainer className="time-input-container">
                  <TimeInput
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={getTimeInputValue('uploadTime', 'minutes')}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      handleTimeInputChange(`uploadTimeMinutes`, value);
                    }}
                    onFocus={(e) => e.target.select()}
                  />
                  <TimeSeparator>:</TimeSeparator>
                  <TimeInput
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={getTimeInputValue('uploadTime', 'seconds')}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      handleTimeInputChange(`uploadTimeSeconds`, value);
                    }}
                    onFocus={(e) => e.target.select()}
                  />
                  <TimeArrowContainer>
                    <TimeArrow
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const totalSeconds = parseInt(formData.upload) || 0;
                        const minutes = Math.floor(totalSeconds / 60);
                        let newSeconds = (totalSeconds % 60 + 1) % 60;
                        const newTotalSeconds = minutes * 60 + newSeconds;
                        setFormData(prev => ({
                          ...prev,
                          upload: String(newTotalSeconds)
                        }));
                      }}
                      className="up"
                    >
                      ▴
                    </TimeArrow>
                    <TimeArrow
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const totalSeconds = parseInt(formData.upload) || 0;
                        const minutes = Math.floor(totalSeconds / 60);
                        let newSeconds = (totalSeconds % 60 - 1 + 60) % 60;
                        const newTotalSeconds = minutes * 60 + newSeconds;
                        setFormData(prev => ({
                          ...prev,
                          upload: String(newTotalSeconds)
                        }));
                      }}
                      className="down"
                    >
                      ▾
                    </TimeArrow>
                  </TimeArrowContainer>
                </TimeInputContainer>
              </TimeInputColumn>

              <TimeInputColumn>
                <div className="time-label"><strong>投票时间</strong></div>
                <TimeInputContainer className="time-input-container">
                  <TimeInput
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={getTimeInputValue('voteTime', 'minutes')}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      handleTimeInputChange(`voteTimeMinutes`, value);
                    }}
                    onFocus={(e) => e.target.select()}
                  />
                  <TimeSeparator>:</TimeSeparator>
                  <TimeInput
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={getTimeInputValue('voteTime', 'seconds')}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      handleTimeInputChange(`voteTimeSeconds`, value);
                    }}
                    onFocus={(e) => e.target.select()}
                  />
                  <TimeArrowContainer>
                    <TimeArrow
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const totalSeconds = parseInt(formData.vote) || 0;
                        const minutes = Math.floor(totalSeconds / 60);
                        let newSeconds = (totalSeconds % 60 + 1) % 60;
                        const newTotalSeconds = minutes * 60 + newSeconds;
                        setFormData(prev => ({
                          ...prev,
                          vote: String(newTotalSeconds)
                        }));
                      }}
                      className="up"
                    >
                      ▴
                    </TimeArrow>
                    <TimeArrow
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const totalSeconds = parseInt(formData.vote) || 0;
                        const minutes = Math.floor(totalSeconds / 60);
                        let newSeconds = (totalSeconds % 60 - 1 + 60) % 60;
                        const newTotalSeconds = minutes * 60 + newSeconds;
                        setFormData(prev => ({
                          ...prev,
                          vote: String(newTotalSeconds)
                        }));
                      }}
                      className="down"
                    >
                      ▾
                    </TimeArrow>
                  </TimeArrowContainer>
                </TimeInputContainer>
              </TimeInputColumn>
            </TimeInputRow>
          </FormGroup>
  
          <FormGroup>
            <FormLabel>设置密码</FormLabel>
            <div className="input-wrapper">
              <FormInput
                  type="text"
                  name="uploadPwd"
                  value={formData.joinPwd}
                  onChange={handleInputChange}
                  onPaste={(e) => {
                    // 允许粘贴任何文本
                    const pastedText = e.clipboardData.getData('text');
                    if (pastedText) {
                      setFormData(prev => ({
                        ...prev,
                        uploadPwd: pastedText
                      }));
                      e.preventDefault();
                    }
                  }}
                  placeholder="请输入参与密码，留空则不需要密码"
              />
            </div>
          </FormGroup>
          
          <InputGroup style={{ marginBottom: '20px' }}>
            <Label style={{ display: 'block', marginBottom: '12px', fontSize: '14px', color: 'var(--text-secondary)' }}>上传设置</Label>
            <div style={{ width: '100%' }}>
              <OptionGrid>
                <RadioWrapper style={{ gap: '2px' }}>
                  <input
                    type="radio"
                    id="limit-one"
                    name="uploadPicCount"
                    checked={formData.uploadPicCount === '1'}
                    onChange={() => handleOptionSelect('uploadPicCount', '1')}
                    style={{ marginRight: '-1px' }}
                  />
                  <label htmlFor="limit-one" style={{ marginLeft: '3px' }}>每人限定 1 张图</label>
                </RadioWrapper>
                <RadioWrapper className="has-input" style={{ gap: '2px', width: '100%' }}>
                  <input
                    type="radio"
                    id="custom-limit"
                    name="uploadPicCount"
                    checked={formData.uploadPicCount !== '1'}
                    onChange={() => {
                      if (formData.uploadPicCount === '1') handleOptionSelect('uploadPicCount', '5');
                    }}
                    style={{ marginRight: '-1px' }}
                  />
                  <label htmlFor="custom-limit" style={{ marginLeft: '3px', display: 'inline-block', width: 'auto' }}>自定义</label>
                  {formData.uploadPicCount !== '1' && (
                    <>
                      <Input
                        type="text"
                        value={formData.uploadPicCount.replace(/\u200B/g, '')}
                        onChange={(e) => {
                          // Get the raw input value
                          const inputValue = e.target.value;

                          // If the value is exactly "1", we need to handle it specially
                          // to prevent the input field from disappearing
                          if (inputValue === '1') {
                            // Use a special marker that won't be visible to users
                            // but will make the string different from '1'
                            handleOptionSelect('uploadPicCount', '1\u200B'); // Using zero-width space
                          } else {
                            handleOptionSelect('uploadPicCount', inputValue);
                          }
                        }}
                        $isDark={isDarkMode}
                        style={{ display: 'inline-block', marginLeft: '2px', marginRight: '2px' }}
                      />
                      <span style={{ fontSize: '14px', color: 'var(--text-primary)', display: 'inline-block', whiteSpace: 'nowrap' }}>张</span>
                    </>
                  )}
                </RadioWrapper>
                <RadioWrapper style={{ gap: '2px' }}>
                  <input
                    type="radio"
                    id="real-name"
                    name="realNameType"
                    checked={formData.realNameType === '1'}
                    onChange={() => handleOptionSelect('realNameType', '1')}
                    style={{ marginRight: '-1px' }}
                  />
                  <label htmlFor="real-name" style={{ marginLeft: '3px' }}>实名上传</label>
                </RadioWrapper>
                <RadioWrapper style={{ gap: '2px' }}>
                  <input
                    type="radio"
                    id="anonymous"
                    name="realNameType"
                    checked={formData.realNameType === '0'}
                    onChange={() => handleOptionSelect('realNameType', '0')}
                    style={{ marginRight: '-1px' }}
                  />
                  <label htmlFor="anonymous" style={{ marginLeft: '3px' }}>匿名上传</label>
                </RadioWrapper>
              </OptionGrid>
            </div>
          </InputGroup>
          
          <InputGroup style={{ marginBottom: '30px' }}>
            <Label style={{ display: 'block', marginBottom: '12px', fontSize: '14px', color: 'var(--text-secondary)' }}>投票设置</Label>
            <div style={{ width: '100%' }}>
              <OptionGrid style={{ gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
                <RadioWrapper className="has-input" style={{ margin: 0, gap: '2px', width: '100%' }}>
                  <input
                    type="radio"
                    id="vote-type-1"
                    name="voteConfType"
                    checked={formData.voteConfType === '1'}
                    onChange={() => handleOptionSelect('voteConfType', '1')}
                    style={{ marginRight: '-1px' }}
                  />
                  <label htmlFor="vote-type-1" style={{ marginLeft: '3px', display: 'inline-block', width: 'auto' }}>自定义</label>
                  {formData.voteConfType === '1' && (
                    <>
                      <Input
                        type="text"
                        value={formData.voteConf1Num.replace(/\u200B/g, '')}
                        onChange={(e) => {
                          // Get the raw input value
                          const inputValue = e.target.value;

                          // If the value is exactly "5", we need to handle it specially
                          if (inputValue === '5') {
                            // Use a special marker that won't be visible to users
                            handleOptionSelect('voteConf1Num', '5\u200B'); // Using zero-width space
                          } else {
                            // For all other values, use them as-is
                            handleOptionSelect('voteConf1Num', inputValue);
                          }
                        }}
                        $isDark={isDarkMode}
                        style={{ display: 'inline-block', marginLeft: '2px', marginRight: '2px' }}
                      />
                      <span style={{ fontSize: '14px', color: 'var(--text-primary)', display: 'inline-block', whiteSpace: 'nowrap' }}>票</span>
                    </>
                  )}
                </RadioWrapper>
                <RadioWrapper className="has-input long-text" style={{ margin: 0, gap: '2px', width: '100%' }}>
                  <input
                    type="radio"
                    id="vote-type-2"
                    name="voteConfType"
                    checked={formData.voteConfType === '2'}
                    onChange={() => handleOptionSelect('voteConfType', '2')}
                    style={{ marginRight: '-1px' }}
                  />
                  {formData.voteConfType === '2' ? (
                    <>
                      <label htmlFor="vote-type-2" style={{ marginLeft: '3px', display: 'inline-block', width: 'auto' }}>每</label>
                      <Input
                        type="text"
                        value={formData.voteConf2Num.replace(/\u200B/g, '')}
                        onChange={(e) => {
                          // Get the raw input value
                          const inputValue = e.target.value;

                          // If the value is exactly "5", we need to handle it specially
                          if (inputValue === '5') {
                            // Use a special marker that won't be visible to users
                            handleOptionSelect('voteConf2Num', '5\u200B'); // Using zero-width space
                          } else {
                            // For all other values, use them as-is
                            handleOptionSelect('voteConf2Num', inputValue);
                          }
                        }}
                        $isDark={isDarkMode}
                        style={{ display: 'inline-block', marginLeft: '2px', marginRight: '2px' }}
                      />
                      <span style={{ fontSize: '14px', color: 'var(--text-primary)', display: 'inline-block', whiteSpace: 'nowrap' }}>位参赛者 1 票</span>
                    </>
                  ) : (
                    <label htmlFor="vote-type-2" style={{ marginLeft: '3px', display: 'inline-block', width: 'auto' }}>
                      每 N 位参赛者 1 票
                    </label>
                  )}
                </RadioWrapper>
                <RadioWrapper style={{ margin: 0, gap: '2px' }}>
                  <input
                    type="radio"
                    id="real-name-vote"
                    name="voteNameType"
                    checked={formData.voteNameType === '1'}
                    onChange={() => handleOptionSelect('voteNameType', '1')}
                    style={{ marginRight: '-1px' }}
                  />
                  <label htmlFor="real-name-vote" style={{ marginLeft: '3px' }}>实名投票</label>
                </RadioWrapper>
                <RadioWrapper style={{ margin: 0, gap: '2px' }}>
                  <input
                    type="radio"
                    id="anonymous-vote"
                    name="voteNameType"
                    checked={formData.voteNameType === '0'}
                    onChange={() => handleOptionSelect('voteNameType', '0')}
                    style={{ marginRight: '-1px' }}
                  />
                  <label htmlFor="anonymous-vote" style={{ marginLeft: '3px' }}>匿名投票</label>
                </RadioWrapper>
              </OptionGrid>
            </div>
          </InputGroup>

          <div style={{ textAlign: 'center', marginTop: '30px', display: 'flex', justifyContent: 'center', gap: '10px' }}>
            <PrimaryButton
              type="submit"
              style={{
                width: '112px',
                height: '36px',
                padding: '7px 24px',
                fontSize: '14px',
                background: 'var(--brand, #FF4B3F)',
                borderRadius: '8px',
                border: 'none',
                boxShadow: 'none',
                transform: 'none'
              }}
            >
              发起对战
            </PrimaryButton>
            <SecondaryButton
              type="button"
              onClick={handleReset}
              style={{
                width: '80px',
                height: '36px',
                padding: '7px 24px',
                fontSize: '14px',
                borderRadius: '8px',
                background: 'transparent',
                border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'var(--text-primary, #111111)'}`,
                color: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'var(--text-primary, #111111)',
                boxShadow: 'none',
                transform: 'none'
              }}
            >
              重置
            </SecondaryButton>
          </div>
        </Form>
      );
    } else {
      // 对战开始后显示操作按钮
      return (
        <Form onSubmit={handleCreateBattle} style={{ margin: '0 auto', overflowX: 'hidden' }} noValidate>
          <ResponsiveInputGroup>
            <ResponsiveLabel>*主题（中文）</ResponsiveLabel>
            <Input
              type="text"
              name="fightNameCn"
              value={formData.fightNameCn}
              onChange={handleInputChange}
              placeholder="请输入"
              style={{
                flex: 1,
                maxWidth: '450px',
                width: '100%',
                '@media (max-width: 768px)': {
                  width: '100%',
                  maxWidth: '100%'
                }
              }}
            />
          </ResponsiveInputGroup>

          <ResponsiveInputGroup>
            <ResponsiveLabel>*主题（英文）</ResponsiveLabel>
            <Input
              type="text"
              name="fightNameEn"
              value={formData.fightNameEn}
              onChange={handleInputChange}
              placeholder="请输入"
              style={{
                flex: 1,
                maxWidth: '450px',
                width: '100%',
                '@media (max-width: 768px)': {
                  width: '100%',
                  maxWidth: '100%'
                }
              }}
            />
          </ResponsiveInputGroup>

          <ResponsiveInputGroup>
            <ResponsiveLabel>描述</ResponsiveLabel>
            <TextArea
              name="fightDesc"
              value={formData.fightDesc}
              onChange={handleInputChange}
              placeholder="请输入"
              style={{
                minHeight: '80px',
                fontSize: '14px',
                flex: 1,
                maxWidth: '450px',
                '@media (max-width: 768px)': {
                  width: '100%',
                  maxWidth: '100%'
                }
              }}
            />
          </ResponsiveInputGroup>

          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '10px',
            marginBottom: '30px',
            width: '100%'
          }}>
            <PrimaryButton
              type="button"
              onClick={handleUpdateBattle}
              style={{
                width: '112px',
                height: '36px',
                padding: '7px 24px',
                fontSize: '14px',
                background: 'var(--brand, #FF4B3F)',
                borderRadius: '8px',
                border: 'none',
                boxShadow: 'none',
                transform: 'none'
              }}
            >
              更新信息
            </PrimaryButton>
          </div>

          <InputGroup style={{ marginBottom: '20px' }}>
            <ResponsiveLabel>上传</ResponsiveLabel>
            <div style={{ flex: 1, maxWidth: '640px' }}>
              <ActionButtonGroup className="upload-buttons" style={{ justifyContent: 'flex-start', margin: 0, padding: 0 }}>
                <SecondaryButton
                  type="button"
                  onClick={() => handleBattleAction(isStop ? 'again' : 'stop')}
                  style={{
                    minWidth: 'auto',
                    maxWidth: 'none',
                    flex: '0 0 auto',
                    margin: '0 10px 0 0',
                    padding: '8px 20px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxShadow: 'none',
                    background: 'transparent',
                    border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'var(--text-primary, #111111)'}`,
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'var(--text-primary, #111111)'
                  }}
                >
                  {isStop ? '继续' : '暂停'}
                </SecondaryButton>
                <SecondaryButton
                  type="button"
                  onClick={() => handleBattleAction('delay', { second: 30 })}
                  style={{
                    minWidth: 'auto',
                    maxWidth: 'none',
                    flex: '0 0 auto',
                    margin: '0 10px 0 0',
                    padding: '8px 20px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxShadow: 'none',
                    background: 'transparent',
                    border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'var(--text-primary, #111111)'}`,
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'var(--text-primary, #111111)'
                  }}
                >
                  延长 30s
                </SecondaryButton>
                <SecondaryButton
                  type="button"
                  onClick={() => handleBattleAction('delay', { second: 60 })}
                  style={{
                    minWidth: 'auto',
                    maxWidth: 'none',
                    flex: '0 0 auto',
                    margin: '0 10px 0 0',
                    padding: '8px 20px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxShadow: 'none',
                    background: 'transparent',
                    border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'var(--text-primary, #111111)'}`,
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'var(--text-primary, #111111)'
                  }}
                >
                  延长 1min
                </SecondaryButton>
                <SecondaryButton
                  type="button"
                  onClick={() => setShowDelayModal(true)}
                  style={{
                    minWidth: 'auto',
                    maxWidth: 'none',
                    flex: '0 0 auto',
                    margin: '0',
                    padding: '8px 20px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxShadow: 'none',
                    background: 'transparent',
                    border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'var(--text-primary, #111111)'}`,
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'var(--text-primary, #111111)'
                  }}
                >
                  自定义延长
                </SecondaryButton>
              </ActionButtonGroup>
            </div>
          </InputGroup>

          <InputGroup style={{ marginBottom: '20px' }}>
            <ResponsiveLabel>对局操作</ResponsiveLabel>
            <div style={{ flex: 1, maxWidth: '640px' }}>
              <ActionButtonGroup className="operation-buttons" style={{ justifyContent: 'flex-start', margin: 0, padding: 0 }}>
                <SecondaryButton
                  type="button"
                  onClick={() => {
                    if (fightStatus === 40) {
                      // 如果是结束阶段，不执行任何操作，因为会自动跳转
                      return;
                    } else {
                      // 否则执行下一阶段操作
                      handleBattleAction('next');
                    }
                  }}
                  disabled={isStop || fightStatus === 40}
                  style={{
                    minWidth: 'auto',
                    maxWidth: 'none',
                    flex: '0 0 auto',
                    margin: '0 10px 0 0',
                    padding: '8px 20px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxShadow: 'none',
                    background: 'transparent',
                    border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'var(--text-primary, #111111)'}`,
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'var(--text-primary, #111111)',
                    opacity: (isStop || fightStatus === 40) ? '0.5' : '1',
                    cursor: (isStop || fightStatus === 40) ? 'not-allowed' : 'pointer',
                    '@media (max-width: 480px)': {
                      margin: '0 6px 0 0',
                      padding: '8px 12px',
                      fontSize: '13px',
                      flex: '1.2'
                    }
                  }}
                >
                  {fightStatus === 40 ? '加载结果中...' : '结束当前阶段'}
                </SecondaryButton>
                <SecondaryButton
                  type="button"
                  onClick={() => handleBattleAction('closes')}
                  style={{
                    minWidth: 'auto',
                    maxWidth: 'none',
                    flex: '0 0 auto',
                    margin: '0',
                    padding: '8px 20px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxShadow: 'none',
                    background: '#ff4d4f',
                    color: '#fff',
                    border: 'none',
                    '@media (max-width: 480px)': {
                      padding: '8px 12px',
                      fontSize: '13px',
                      flex: '1'
                    }
                  }}
                >
                  结束整轮对战
                </SecondaryButton>
              </ActionButtonGroup>
            </div>
          </InputGroup>
        </Form>
      );
    }
  };

  const renderWorksContent = () => {
    if (fightStatus === 0) {
      return (
        <EmptyState>
          <div className="message">当前无对战进行中</div>
        </EmptyState>
      );
    }

    if (fightStatus === 10) {
      return (
        <EmptyState>
          <div className="message">创作阶段进行中，请等待作品上传</div>
        </EmptyState>
      );
    }

    if (creations.length === 0) {
      return (
        <EmptyState>
          <div className="message">暂无作品上传</div>
        </EmptyState>
      );
    }

    return (
      <WorksGrid>
        {creations.map((work) => (
          <WorkCard key={work.creationId} $isDark={isDarkMode}>
            <img
              className="work-image"
              src={work.creationUrl}
              alt="作品"
              onClick={() => window.open(work.creationUrl, '_blank')}
            />
            <div className="work-info">
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%'
              }}>
                {work.userName && (
                  <div className="author">
                    {work.userPic && <img src={processImageUrl(work.userPic, true)} alt={work.userName} />}
                    <span>{work.userName}</span>
                  </div>
                )}
                {fightStatus === 30 && (
                  <div className="votes" style={{ marginLeft: 'auto' }}>
                    <span className="vote-count">票数：{work.voteNumber}</span>
                  </div>
                )}
              </div>
            </div>
          </WorkCard>
        ))}
      </WorksGrid>
    );
  };

  // 添加自动获取焦点的 effect
  useEffect(() => {
    if (fileUploadRef.current) {
      fileUploadRef.current.focus();
    }
  }, [file]);

  // 添加全局粘贴事件监听
  useEffect(() => {
    const handleGlobalPaste = (e) => {
      if (!file) {  // 只在没有文件时处理粘贴
        handlePaste(e);
      }
    };

    document.addEventListener('paste', handleGlobalPaste);
    return () => {
      document.removeEventListener('paste', handleGlobalPaste);
    };
  }, [file, handlePaste]);

  // 添加键盘事件监听
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (file) {
          handleRemoveFile();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [file]);

  return (
    <Container>
      <GlobalResponsiveStyles>
        {showDelayModal && (
          <Modal>
            <ModalContent>
              <ModalTitle>
                延长倒计时
                <ModalCloseButton onClick={() => setShowDelayModal(false)}>×</ModalCloseButton>
              </ModalTitle>
              <ModalInputGroup>
                <Label style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>自定义时长：</Label>
                <ModalInput
                  type="text"
                  value={delaySeconds}
                  onChange={(e) => setDelaySeconds(e.target.value)}
                  placeholder="请输入"
                />
                <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>秒</span>
              </ModalInputGroup>
              <ModalButtons>
                <ModalButton className="cancel" onClick={() => setShowDelayModal(false)}>
                  取消
                </ModalButton>
                <ModalButton className="confirm" onClick={handleDelayConfirm}>
                  确认
                </ModalButton>
              </ModalButtons>
            </ModalContent>
          </Modal>
        )}
      </GlobalResponsiveStyles>

      <div className="status-info">
        <div className="theme-title">主题</div>
        <div className="theme-content">
          {(fightStatus !== 0 && fightStatus !== 40) ? (fightInfo?.fightNameCn || '当前未设置对战主题') : '当前未设置对战主题'}
        </div>
        <div className="status">{getStatusText(fightStatus)}</div>
        <TimerDisplay $isPaused={isStop === 1} className={isStop === 1 ? 'timer-paused' : ''}>
          {localLastSecond <= 0 && (fightStatus === 20 || fightStatus === 30)
            ? (fightStatus === 20 ? '等待上传完成' : '等待投票完成')
            : formatTimeWithSeparators(formatCountdown(Math.max(0, localLastSecond)))}
          {isStop === 1 && <PauseIndicator>（已暂停）</PauseIndicator>}
        </TimerDisplay>
        <FightDrainStatus />
        <OnlineCount className="online-count" onClick={handleOnlineCountClick}>
          <div style={{
            fontWeight: 'bold',
            color: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'var(--text-primary, #111111)'
          }}>
            在线人数：{onlineUsers.length}人
          </div>
          <div className="online-avatars">
            {onlineUsers.slice(0, 5).map((user, index) => (
              <img
                key={user.userId}
                src={processImageUrl(user.userPic, true)}
                alt={user.userName}
                title={user.userName}
                style={{
                  zIndex: 5 - index,
                  objectFit: 'cover',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  border: '2px solid #fff',
                  marginLeft: index > 0 ? '-8px' : '0',
                }}
              />
            ))}
          </div>
          {showOnlineUsers && (
            <OnlineUsersList className="online-users-list">
              {onlineUsers.map((user) => (
                <OnlineUser key={user.userId}>
                  <img
                    src={processImageUrl(user.userPic, true)}
                    alt={user.userName}
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      objectFit: 'cover'
                    }}
                  />
                  <span style={{
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : '#333',
                    fontSize: '14px',
                    marginLeft: '8px'
                  }}>
                    {user.userName}
                  </span>
                </OnlineUser>
              ))}
            </OnlineUsersList>
          )}
        </OnlineCount>
      </div>

      <AdminQueuePanel />
      <TabContainer>
        <Tab
          $active={activeTab === 'works'}
          onClick={(e) => {
            e.preventDefault();
            setActiveTab('works');
          }}
        >
          作品
        </Tab>
        <Tab
          $active={activeTab === 'operation'}
          onClick={(e) => {
            e.preventDefault();
            setActiveTab('operation');
          }}
          $isOperation
        >
          操作
        </Tab>
      </TabContainer>

      <FormBottomContainer>
        {activeTab === 'works' ? renderWorksContent() : renderOperationContent()}
      </FormBottomContainer>
    </Container>
  );
};

export default BattleSettings;
